import React, { useState, useEffect } from 'react';
import { writingApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
    Flame, Target, Calendar, TrendingUp, Award, Settings, ChevronLeft
} from 'lucide-react';
import { toast } from 'sonner';

const WritingStreak = ({ onBack }) => {
    const [streak, setStreak] = useState(null);
    const [history, setHistory] = useState([]);
    const [goals, setGoals] = useState({ daily_word_goal: 1000, weekly_word_goal: 5000 });
    const [showGoalSettings, setShowGoalSettings] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [streakRes, historyRes, goalsRes] = await Promise.all([
                writingApi.getStreak(),
                writingApi.getHistory(30),
                writingApi.getGoals(),
            ]);
            setStreak(streakRes.data);
            setHistory(historyRes.data);
            setGoals(goalsRes.data);
        } catch (error) {
            console.error('Failed to load streak data');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateGoals = async (e) => {
        e.preventDefault();
        try {
            await writingApi.updateGoals(goals);
            toast.success('Goals updated!');
            setShowGoalSettings(false);
            loadData();
        } catch (error) {
            toast.error('Failed to update goals');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-pulse text-muted-foreground">Loading streak data...</div>
            </div>
        );
    }

    // Generate calendar heatmap data for last 30 days
    const generateHeatmapData = () => {
        const days = [];
        const today = new Date();
        for (let i = 29; i >= 0; i--) {
            const date = new Date(today);
            date.setDate(date.getDate() - i);
            const dateStr = date.toISOString().split('T')[0];
            const log = history.find(h => h.date === dateStr);
            days.push({
                date: dateStr,
                words: log?.words_written || 0,
                day: date.toLocaleDateString('en', { weekday: 'short' }).charAt(0)
            });
        }
        return days;
    };

    const heatmapData = generateHeatmapData();

    const getHeatmapColor = (words) => {
        if (words === 0) return 'bg-muted';
        if (words < goals.daily_word_goal * 0.25) return 'bg-accent/20';
        if (words < goals.daily_word_goal * 0.5) return 'bg-accent/40';
        if (words < goals.daily_word_goal * 0.75) return 'bg-accent/60';
        if (words < goals.daily_word_goal) return 'bg-accent/80';
        return 'bg-accent';
    };

    return (
        <div className="min-h-screen bg-background">
            <header className="border-b border-border">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" onClick={onBack} data-testid="back-btn">
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Back
                        </Button>
                        <div>
                            <h1 className="font-serif text-2xl font-semibold">Writing Streak</h1>
                            <p className="text-sm text-muted-foreground">Track your progress and stay motivated</p>
                        </div>
                    </div>
                    <Dialog open={showGoalSettings} onOpenChange={setShowGoalSettings}>
                        <DialogTrigger asChild>
                            <Button variant="outline" data-testid="goal-settings-btn">
                                <Settings className="h-4 w-4 mr-2" />
                                Set Goals
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Writing Goals</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleUpdateGoals} className="space-y-4 mt-4">
                                <div className="space-y-2">
                                    <Label htmlFor="daily">Daily Word Goal</Label>
                                    <Input
                                        id="daily"
                                        type="number"
                                        value={goals.daily_word_goal}
                                        onChange={(e) => setGoals({ ...goals, daily_word_goal: parseInt(e.target.value) })}
                                        data-testid="daily-goal-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="weekly">Weekly Word Goal</Label>
                                    <Input
                                        id="weekly"
                                        type="number"
                                        value={goals.weekly_word_goal}
                                        onChange={(e) => setGoals({ ...goals, weekly_word_goal: parseInt(e.target.value) })}
                                        data-testid="weekly-goal-input"
                                    />
                                </div>
                                <div className="flex justify-end gap-3">
                                    <Button type="button" variant="outline" onClick={() => setShowGoalSettings(false)}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" data-testid="save-goals-btn">Save Goals</Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-6 py-8">
                {/* Streak Stats */}
                <div className="grid md:grid-cols-4 gap-4 mb-8">
                    <Card className="hover-lift">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                                    <Flame className="h-6 w-6 text-accent" />
                                </div>
                                <div>
                                    <div className="font-serif text-3xl font-bold">{streak?.current_streak || 0}</div>
                                    <div className="text-sm text-muted-foreground">Day Streak</div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="hover-lift">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                                    <TrendingUp className="h-6 w-6 text-success" />
                                </div>
                                <div>
                                    <div className="font-serif text-3xl font-bold">{streak?.longest_streak || 0}</div>
                                    <div className="text-sm text-muted-foreground">Best Streak</div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="hover-lift">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center">
                                    <Target className="h-6 w-6 text-warning" />
                                </div>
                                <div>
                                    <div className="font-serif text-3xl font-bold">{streak?.words_today?.toLocaleString() || 0}</div>
                                    <div className="text-sm text-muted-foreground">Words Today</div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="hover-lift">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-full bg-ai-magic/10 flex items-center justify-center">
                                    <Calendar className="h-6 w-6 text-ai-magic" />
                                </div>
                                <div>
                                    <div className="font-serif text-3xl font-bold">{streak?.words_this_week?.toLocaleString() || 0}</div>
                                    <div className="text-sm text-muted-foreground">This Week</div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Progress Bars */}
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-serif text-lg">Daily Progress</CardTitle>
                            <CardDescription>
                                {streak?.words_today?.toLocaleString() || 0} / {streak?.daily_goal?.toLocaleString() || 1000} words
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Progress value={streak?.daily_progress || 0} className="h-3" />
                            <p className="text-sm text-muted-foreground mt-2">
                                {streak?.daily_progress >= 100 
                                    ? "Daily goal achieved! Great work!" 
                                    : `${Math.max(0, (streak?.daily_goal || 1000) - (streak?.words_today || 0)).toLocaleString()} words to go`
                                }
                            </p>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="font-serif text-lg">Weekly Progress</CardTitle>
                            <CardDescription>
                                {streak?.words_this_week?.toLocaleString() || 0} / {streak?.weekly_goal?.toLocaleString() || 5000} words
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Progress value={streak?.weekly_progress || 0} className="h-3" />
                            <p className="text-sm text-muted-foreground mt-2">
                                {streak?.weekly_progress >= 100 
                                    ? "Weekly goal achieved! Amazing dedication!" 
                                    : `${Math.max(0, (streak?.weekly_goal || 5000) - (streak?.words_this_week || 0)).toLocaleString()} words to go`
                                }
                            </p>
                        </CardContent>
                    </Card>
                </div>

                {/* Activity Heatmap */}
                <Card className="mb-8">
                    <CardHeader>
                        <CardTitle className="font-serif text-lg">30-Day Activity</CardTitle>
                        <CardDescription>Your writing activity over the last month</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-wrap gap-1">
                            {heatmapData.map((day, i) => (
                                <div
                                    key={i}
                                    className={`w-6 h-6 rounded-sm ${getHeatmapColor(day.words)} cursor-pointer transition-transform hover:scale-110`}
                                    title={`${day.date}: ${day.words.toLocaleString()} words`}
                                    data-testid={`heatmap-day-${i}`}
                                />
                            ))}
                        </div>
                        <div className="flex items-center gap-2 mt-4 text-xs text-muted-foreground">
                            <span>Less</span>
                            <div className="flex gap-1">
                                <div className="w-4 h-4 rounded-sm bg-muted" />
                                <div className="w-4 h-4 rounded-sm bg-accent/20" />
                                <div className="w-4 h-4 rounded-sm bg-accent/40" />
                                <div className="w-4 h-4 rounded-sm bg-accent/60" />
                                <div className="w-4 h-4 rounded-sm bg-accent/80" />
                                <div className="w-4 h-4 rounded-sm bg-accent" />
                            </div>
                            <span>More</span>
                        </div>
                    </CardContent>
                </Card>

                {/* Badges/Achievements */}
                <Card>
                    <CardHeader>
                        <CardTitle className="font-serif text-lg flex items-center gap-2">
                            <Award className="h-5 w-5 text-accent" />
                            Achievements
                        </CardTitle>
                        <CardDescription>Badges earned through your writing journey</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {streak?.badges?.length > 0 ? (
                            <div className="flex flex-wrap gap-3">
                                {streak.badges.map((badge) => (
                                    <div 
                                        key={badge.id}
                                        className="flex items-center gap-2 p-3 bg-accent/10 rounded-lg"
                                        data-testid={`badge-${badge.id}`}
                                    >
                                        <Award className="h-5 w-5 text-accent" />
                                        <div>
                                            <div className="font-medium text-sm">{badge.name}</div>
                                            <div className="text-xs text-muted-foreground">{badge.description}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <Award className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                                <p className="text-muted-foreground">Keep writing to earn badges!</p>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Write for 7 consecutive days to earn your first badge
                                </p>
                            </div>
                        )}

                        {/* Available Badges */}
                        <div className="mt-6 pt-6 border-t border-border">
                            <h4 className="font-medium text-sm mb-3">Available Badges</h4>
                            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                                <BadgePreview 
                                    name="Week Warrior" 
                                    description="7-day writing streak" 
                                    earned={streak?.current_streak >= 7}
                                />
                                <BadgePreview 
                                    name="Monthly Master" 
                                    description="30-day writing streak" 
                                    earned={streak?.current_streak >= 30}
                                />
                                <BadgePreview 
                                    name="Century Scribe" 
                                    description="100-day writing streak" 
                                    earned={streak?.current_streak >= 100}
                                />
                                <BadgePreview 
                                    name="Daily Goal" 
                                    description="Hit daily word target" 
                                    earned={streak?.daily_progress >= 100}
                                />
                                <BadgePreview 
                                    name="Weekly Goal" 
                                    description="Hit weekly word target" 
                                    earned={streak?.weekly_progress >= 100}
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </main>
        </div>
    );
};

const BadgePreview = ({ name, description, earned }) => (
    <div className={`p-3 rounded-lg border ${earned ? 'border-accent bg-accent/5' : 'border-border bg-muted/30 opacity-50'}`}>
        <div className="flex items-center gap-2">
            <Award className={`h-4 w-4 ${earned ? 'text-accent' : 'text-muted-foreground'}`} />
            <div>
                <div className="font-medium text-sm">{name}</div>
                <div className="text-xs text-muted-foreground">{description}</div>
            </div>
        </div>
    </div>
);

export default WritingStreak;
